import { useState, useEffect } from 'react';
import { FaCopy, FaCheck } from 'react-icons/fa';

// Admin connector credentials (§5.2.3, admin side).
//
// NO STEP-UP, deliberately (Bill, 2026-08-06). The admin pool is
// MfaConfiguration: ON, so every admin session has already passed TOTP at login
// and a second proof would add friction without adding a factor. The CLIENT
// modal does step up, because that pool is OPTIONAL. The asymmetry is the pool
// configuration, not an oversight.
//
// Still fetched on demand rather than on open: DescribeUserPoolClient shares a
// 15 RPS account-wide, non-adjustable bucket, and a secret should not sit in a
// response nobody asked for (§5.2.2.1 rule 4).
//
// There is no parameter naming a user. The server derives the client id from
// the username in the caller's own verified token.
const ConnectClaudeAdminModal = ({ isOpen, onClose, apiUrl }) => {
    const [creds, setCreds] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState('');

    // Never let a secret survive a dismissal.
    useEffect(() => {
        setCreds(null);
        setError('');
        setCopied('');
    }, [isOpen]);

    if (!isOpen) return null;

    const copy = async (value, label) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(label);
            setTimeout(() => setCopied(''), 2000);
        } catch {
            setError('Could not copy to the clipboard - select the text and copy manually.');
        }
    };

    const load = async () => {
        setError('');
        setBusy(true);
        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                setError('Your session has expired. Log out and back in, then retry.');
                return;
            }
            const res = await fetch(`${apiUrl}/admin/mcp/credentials`, {
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.message || data.error || 'Could not retrieve your connector credentials.');
                return;
            }
            setCreds(data);
        } catch {
            setError('Could not retrieve your connector credentials.');
        } finally {
            setBusy(false);
        }
    };

    const CopyRow = ({ label, value, name }) => (
        <div className="mb-3">
            <div className="text-xs font-medium text-gray-500 mb-1">{label}</div>
            <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-gray-100 border border-gray-200 rounded px-2 py-1.5 break-all">
                    {value}
                </code>
                <button
                    type="button"
                    onClick={() => copy(value, name)}
                    className="text-sm px-2 py-1.5 bg-gray-100 text-gray-600 border border-gray-300 rounded hover:bg-gray-200"
                    title={`Copy ${label.toLowerCase()}`}
                >
                    {copied === name ? <FaCheck size={12} className="text-green-600" /> : <FaCopy size={12} />}
                </button>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-3 border-b">
                    <h2 className="text-lg font-semibold text-gray-800">Connect Claude</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                </div>

                <div className="p-5">
                    <p className="text-sm text-gray-600 mb-3">
                        Work on templates, scripts and any project from Claude, on your phone
                        or laptop, without opening the portal.
                    </p>
                    <p className="text-sm text-gray-600 mb-4">
                        In Claude: Settings → Connectors → Add custom connector, then paste these
                        under Advanced settings.
                    </p>

                    {creds ? (
                        <>
                            <CopyRow label="Server URL" value={creds.serverUrl} name="url" />
                            <CopyRow label="Client ID" value={creds.clientId} name="id" />
                            <CopyRow label="Client secret" value={creds.clientSecret} name="secret" />
                            <p className="text-xs text-gray-600 mt-4">
                                This connector can <strong>read everything and change templates and
                                scripts</strong>. Every change asks you to confirm it first.
                            </p>
                            <p className="text-xs text-gray-600 mt-2">
                                Lost the secret? It can&apos;t be shown again or reset — it has to be
                                replaced, and you re-add the connector.
                            </p>
                        </>
                    ) : (
                        <button
                            onClick={load}
                            disabled={busy}
                            className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
                        >
                            {busy ? 'Loading…' : 'Show my credentials'}
                        </button>
                    )}

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mt-4">
                            {error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConnectClaudeAdminModal;
